import { ICameraInput } from '@babylonjs/core/Cameras/cameraInputsManager';
import { IWheelEvent } from '@babylonjs/core/Events/deviceInputEvents';
import { PointerEventTypes, PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import { Epsilon, Scalar } from '@babylonjs/core/Maths';
import { EventState, Observer } from '@babylonjs/core/Misc/observable';
import { Nullable } from '@babylonjs/core/types';
import { KbArcRotateCamera } from './kb-arc-rotate-camera';

/**
 * We are overriding the default mouse wheet input for the arc rotate camera here because
 * the default one does not act correctly when zoomed in close to the target:  it
 * calculates the inertia based on the radius at the time the event happens.  The result
 * is that zooming in is fast than zooming out.  They had a misguided attempt at fixing
 * that here:  https://github.com/BabylonJS/Babylon.js/issues/6215 but it is inadequate.
 * See comments below for where we changed the code
 */
export class KbArcRotateCameraMouseWheelInput implements ICameraInput<KbArcRotateCamera> {
    public camera: KbArcRotateCamera;
    public wheelPrecision = 3.0;
    public wheelDeltaPercentage = 0;

    private _wheel: Nullable<(p: PointerInfo, s: EventState) => void>;
    private _observer: Nullable<Observer<PointerInfo>>;

    private computeDeltaFromMouseWheelLegacyEvent(mouseWheelDelta: number, radius: number) {
        let delta = 0;
        const wheelDelta = mouseWheelDelta * 0.01 * this.wheelDeltaPercentage * radius;
        if (mouseWheelDelta > 0) {
            delta = wheelDelta / (1.0 + this.wheelDeltaPercentage);
        } else {
            delta = wheelDelta * (1.0 + this.wheelDeltaPercentage);
        }
        return delta;
    }

    public attachControl(noPreventDefault?: boolean): void {
        this._wheel = (p, s) => {
            if (p.type !== PointerEventTypes.POINTERWHEEL) {
                return;
            }
            const event = p.event as IWheelEvent;
            let delta = 0;

            const mouseWheelLegacyEvent = event as any;
            let wheelDelta = 0;

            if (mouseWheelLegacyEvent.wheelDelta) {
                wheelDelta = mouseWheelLegacyEvent.wheelDelta;
            } else {
                wheelDelta = -(event.deltaY || event.detail || 0) * 60;
            }

            if (this.wheelDeltaPercentage) {
                delta = this.computeDeltaFromMouseWheelLegacyEvent(wheelDelta, this.camera.radius);

                // If zooming in, estimate the target radius and use that to compute the delta for inertia
                // this will stop multiple scroll events zooming in from adding too much inertia
                //if (delta > 0) {  //KB Note:  their misguided attempt at fixing the problem only affected zoom in, when it should affect both, so we commented this line out
                let estimatedTargetRadius = this.camera.radius;
                let targetInertia = this.camera.inertialRadiusOffset + delta;
                for (let i = 0; i < 20 && Math.abs(targetInertia) > this.camera.speed * Epsilon; i++) {
                    //KB Note:  fixed the lower limit where it opts out to match the camera's own intertial calculation
                    estimatedTargetRadius -= targetInertia;
                    targetInertia *= this.camera.inertia;
                }
                estimatedTargetRadius = Scalar.Clamp(estimatedTargetRadius, 0, Number.MAX_VALUE);
                delta = this.computeDeltaFromMouseWheelLegacyEvent(wheelDelta, estimatedTargetRadius);
                //}
            } else {
                delta = wheelDelta / (this.wheelPrecision * 40);
            }

            if (delta) {
                this.camera.inertialRadiusOffset += delta;
            }

            if (event.preventDefault) {
                if (!noPreventDefault) {
                    event.preventDefault();
                }
            }
        };

        this._observer = this.camera.getScene().onPointerObservable.add(this._wheel, PointerEventTypes.POINTERWHEEL);
    }

    public detachControl(): void {
        if (this._observer) {
            this.camera.getScene().onPointerObservable.remove(this._observer);
            this._observer = null;
            this._wheel = null;
        }
    }

    public getClassName(): string {
        return 'KbArcRotateCameraMouseWheelInput';
    }

    public getSimpleName(): string {
        return 'mousewheel';
    }
}
